import { defineStore, storeToRefs } from "pinia";
import { ref, computed, watch } from "vue";
import { useStorage } from "@vueuse/core";
import { useRoute } from "vue-router";
import { useOciSession } from "@/shared/auth/useOciSession.composable";
import { useAccountStore } from "@/shared/stores/AccountStore";
import { useAppStateStore } from "@/shared/stores/AppStateStore";
import type { AccountEmailAddress } from "@/shared/@types/AccountEmailAddress";
import { AppIdentifier } from "@/shared/@types/AppIdentifier";
import type { CustomerBase } from "@/shared/@types/CustomerBase";
import { type Customer, mapToCustomerType } from "@/shared/@types/Customer";
import type { CustomerAdditionalInfo } from "@/shared/@types/CustomerAdditionalInfo";
import type { CustomerType } from "@/shared/@types/CustomerType";
import {
  type SalesOrganizationId,
  mapToSalesOrganizationId,
} from "@/shared/@types/SalesOrganizationId";
import { FeatureFlag } from "@/shared/@types/FeatureFlag";
import { type WebStore } from "@/shared/@types/WebStore";
import { mapToWebStoreId } from "@/shared/@types/WebStoreId";
import {
  updateInvoiceEmail as updateInvoiceEmailService,
  serializeDataForUpdateInvoiceEmail,
} from "@/shared/services/updateInvoiceEmail.service";
import { useGetCustomer } from "@/shared/composables/useGetCustomer.composable";
import { useGetAccountAddresses } from "@/shared/composables/useGetAccountAddresses.composable";
import { getCustomerRoles } from "@/shared/helper/getCustomerRoles.helper";
import { getInitialSelectedCustomerId } from "@/shared/helper/getInitialSelectedCustomerId.helper";
import { useFeatureFlagsStore } from "@/shared/stores/FeatureFlagsStore";

export const useSelectedCustomerStore = defineStore("SelectedCustomer", () => {
  const route = useRoute();

  const accountStore = useAccountStore();
  const appStateStore = useAppStateStore();
  const { user, allCustomers } = storeToRefs(accountStore);
  const featureFlagsStore = useFeatureFlagsStore();

  const selectedCustomerId = useStorage<string | undefined>(
    "selectedCustomerId",
    undefined,
  );
  const selectedCustomerBase = ref<CustomerBase | undefined>(undefined);
  const selectedCustomer = ref<Customer | undefined>(undefined);
  const selectedCustomerInitiallyFetched = ref<boolean>(false);
  const isInitiallyFetched = computed(
    () => selectedCustomerInitiallyFetched.value,
  );

  const isTcoCalculatorEnabled = computed(() =>
    featureFlagsStore.isFeatureEnabled(FeatureFlag.EquivoltTcoCalculator),
  );

  const customerRoles = computed(() =>
    !user.value || !selectedCustomerId.value
      ? []
      : getCustomerRoles(
          user.value.permissions,
          selectedCustomerId.value,
          isTcoCalculatorEnabled.value,
        ),
  );

  const currentSalesOrganizationId = ref<SalesOrganizationId | undefined>(
    undefined,
  );
  const webStores = ref<Array<WebStore>>([]);
  const defaultWebStores = computed(() =>
    webStores.value.filter((webStore) => webStore.type === "default"),
  );
  const globalGasWebStores = computed(() =>
    webStores.value.filter((webStore) => webStore.type === "global-gas"),
  );
  const selectedWebStoreId = computed(() =>
    route.params?.webStoreId
      ? mapToWebStoreId(route.params?.webStoreId as string)
      : undefined,
  );

  const invoiceEmail = computed<AccountEmailAddress | undefined>(
    () => selectedCustomer.value?.invoiceEmail,
  );
  const deliveryNoteEmails = computed<AccountEmailAddress[]>(
    () => selectedCustomer.value?.deliveryNoteEmails || [],
  );
  const safetyDataEmails = computed<AccountEmailAddress[]>(
    () => selectedCustomer.value?.safetyDataEmails || [],
  );
  const assignedBusinessAreas = computed<string[]>(
    () => selectedCustomer.value?.assignedBusinessAreas || [],
  );
  const customerType = computed<CustomerType | undefined>(
    () => selectedCustomer.value?.type,
  );
  const additionalInfo = computed<CustomerAdditionalInfo | undefined>(
    () => selectedCustomer.value?.additionalInfo,
  );

  const getSelectedCustomerIdByNumber = (customerNumber: string) => {
    const customer = allCustomers.value.find(
      (customer) => customer.number === customerNumber,
    );
    return customer?.id;
  };

  const setSelectedCustomerId = (customerId?: string) => {
    selectedCustomerId.value = customerId;
    featureFlagsStore.resetData();
    selectedCustomerBase.value = allCustomers.value.find(
      (customer) => customer.id === customerId,
    );
    // TODO: Remove this mapping once we can mock the getCustomer service and add general error handling for the case that result is undefined
    // Currently we need this to guarantee that the result is always defined in local development
    selectedCustomer.value = selectedCustomerBase.value
      ? mapToCustomerType(undefined, selectedCustomerBase.value)
      : undefined;
  };

  const setInitialSelectedCustomerId = (currentApp?: AppIdentifier) => {
    if (!user.value) return;
    const initialSelectedCustomerId = getInitialSelectedCustomerId(
      user.value,
      allCustomers.value,
      selectedCustomerId.value,
      currentApp,
    );
    setSelectedCustomerId(initialSelectedCustomerId);
  };

  const {
    customer: fetchedCustomer,
    fetchingCustomerError: fetchingSelectedCustomerError,
    onFetchingCustomer,
  } = useGetCustomer(selectedCustomerBase);

  const resetData = () => {
    selectedCustomerId.value = undefined;
    selectedCustomerBase.value = undefined;
    selectedCustomer.value = undefined;
    selectedCustomerInitiallyFetched.value = false;
    isFetchingSelectedCustomer.value = false;
    fetchingSelectedCustomerError.value = undefined;
    webStores.value = [];
    currentSalesOrganizationId.value = undefined;
    resetAccountAddressData();
    featureFlagsStore.resetData();
  };

  // --- Web Stores and Sales Organization ---
  const setWebStores = (stores: Array<WebStore>) => {
    webStores.value = stores;

    // If there is only one web store available to the user, set its salesOrganizationId as the currentSalesOrganizationId
    // because when on the Dashboard for example it cannot be taken it from the selectedWebStoreId
    if (webStores.value.length === 1) {
      currentSalesOrganizationId.value = mapToSalesOrganizationId(
        webStores.value[0]!.salesOrganizationId,
      );
    }
  };

  const setSalesOrganizationIdByWebStore = (webStoreId: string) => {
    const salesOrganizationId = webStores.value.find(
      (webStore) => webStore.id === webStoreId,
    )?.salesOrganizationId;
    if (salesOrganizationId) {
      currentSalesOrganizationId.value =
        mapToSalesOrganizationId(salesOrganizationId);
    } else {
      currentSalesOrganizationId.value = undefined;
    }
  };

  watch(selectedWebStoreId, (newId) => {
    if (newId) {
      setSalesOrganizationIdByWebStore(newId);
    }
  });

  // --- Account Addresses ---
  const {
    shippingAddresses,
    billingAddresses,
    onFetchingAccountAddresses,
    resetAccountAddressData,
  } = useGetAccountAddresses(
    user,
    selectedCustomerId,
    currentSalesOrganizationId,
  );

  // --- Selected Customer ---
  const isFetchingSelectedCustomer = ref(false);
  const fetchSelectedCustomer = async ({
    throwError = false,
    resetDataOnError = false,
  }: {
    throwError?: boolean;
    resetDataOnError?: boolean;
  } = {}) => {
    if (!user.value || !selectedCustomerId.value) return;

    isFetchingSelectedCustomer.value = true;
    appStateStore.showLoadingOverlay();

    try {
      await onFetchingCustomer({
        userId: user.value.userId,
        customerId: selectedCustomerId.value,
        throwError,
      });

      if (!fetchingSelectedCustomerError.value) {
        selectedCustomer.value = fetchedCustomer.value;
        selectedCustomerInitiallyFetched.value = true;
        setWebStores(fetchedCustomer.value?.webStores || []);
        await featureFlagsStore.onFetchingFeatureFlags(
          selectedCustomerId.value,
        );
        // OCI users get the browser language instead of the Salesforce user language.
        // The OCI session can only be determined here, as it depends on the roles of the selected customer.
        const { isOciSession } = useOciSession();
        if (isOciSession.value) {
          accountStore.setBrowserLanguage();
        } else if (fetchedCustomer.value?.user.localeCode) {
          // TODO: Remove this when language is provided by user itself
          accountStore.setUserLanguage(fetchedCustomer.value.user.localeCode);
        }
        // QUICKFIX: Fetch addresses after fetching selected customer (because sometimes causing a timeout)
        await onFetchingAccountAddresses();
      } else if (resetDataOnError) {
        // Reset data on error to avoid inconsistent state
        resetData();
      }
    } finally {
      isFetchingSelectedCustomer.value = false;
      appStateStore.hideLoadingOverlay();
    }
  };

  // --- Invoice Email ---
  const isUpdatingInvoiceEmail = ref(false);
  const updateInvoiceEmailError = ref();

  const updateInvoiceEmail = async (email: string) => {
    if (!user.value || !selectedCustomerId.value) return;

    try {
      updateInvoiceEmailError.value = undefined;
      isUpdatingInvoiceEmail.value = true;

      await updateInvoiceEmailService({
        userId: user.value.userId,
        customerId: selectedCustomerId.value,
        data: serializeDataForUpdateInvoiceEmail(email),
      });
      // Update selected customer on updating invoice email
      await fetchSelectedCustomer();
    } catch (error) {
      updateInvoiceEmailError.value = error as string;
    } finally {
      isUpdatingInvoiceEmail.value = false;
    }
  };

  return {
    selectedCustomerId,
    selectedCustomer,
    customerRoles,
    getSelectedCustomerIdByNumber,
    setSelectedCustomerId,
    setInitialSelectedCustomerId,
    isInitiallyFetched,
    isFetchingSelectedCustomer,
    fetchingSelectedCustomerError,
    webStores,
    defaultWebStores,
    globalGasWebStores,
    selectedWebStoreId,
    currentSalesOrganizationId,
    shippingAddresses,
    billingAddresses,
    invoiceEmail,
    isUpdatingInvoiceEmail,
    updateInvoiceEmailError,
    deliveryNoteEmails,
    safetyDataEmails,
    assignedBusinessAreas,
    customerType,
    additionalInfo,
    fetchSelectedCustomer,
    resetData,
    updateInvoiceEmail,
  };
});
